import createReducer from 'redux/createReducer';
import type {
  Eligibility,
  ItemId,
  SearchTermsResponse,
} from 'api/definitions/intelligent-shopping-experience/types';
import { REASON_NOT_IN_COHORT, STATUS_INELIGIBLE } from 'constants/shop-by-list';
import {
  GET_SHOP_BY_LIST_EXTRACTED_SEARCH_TERMS,
  GET_SHOP_BY_LIST_SUGGESTIONS,
  GET_SHOP_BY_LIST_ELIGIBILITY,
  UPDATE_SHOP_BY_LIST_SEARCH_TERM,
  GET_SHOP_BY_LIST_ESTIMATED_PRICE,
  FETCH_VIEW_ALL_MODAL_SUGGESTIONS,
  RESET_VIEW_ALL_MODAL_SUGGESTIONS,
  ADD_VIEW_ALL_MODAL_PRODUCT_TO_CAROUSEL,
} from 'redux/modules/shop-by-list/actions/types';
import {
  ShopByListProcessingPhase,
  ShopByListSuggestionsStatus,
  type ShopByListItemState,
  type ShopByListState,
  type ShopByListProcessingActiveState,
  type UpdateShopByListItem,
} from 'redux/modules/shop-by-list/types';
import { initialState } from 'redux/modules/shop-by-list/state';
import type { LegacyProductReference, LineNumber } from 'constants/products';
import type { RecommendedSelectionWithKey } from 'redux/transforms/types';
import { SEARCH_TERM_TYPE, SEARCH_TYPE } from 'analytics/list-search-tracking';
import { Pricing2PricedProductsResponse } from 'api/definitions/pricing';

type ShopByListEligibilitySuccessAction = {
  result: Eligibility;
};

type GetExtractedSearchTermsRequestAction = {
  searchTermType: SEARCH_TERM_TYPE;
  searchType: SEARCH_TYPE;
};

type GetExtractedSearchTermsSuccessAction = {
  result: SearchTermsResponse;
  searchTermType: SEARCH_TERM_TYPE;
  searchType: SEARCH_TYPE;
};

type TransformedSuggestion = {
  itemId: ItemId;
  candidates: LegacyProductReference[];
};

export type TransformedSuggestionsResponse = {
  suggestions: TransformedSuggestion[];
  recommendedSelection: RecommendedSelectionWithKey[];
};

type GetSuggestionsSuccessAction = {
  result: TransformedSuggestionsResponse;
};

type UpdateSearchTermAction = {
  payload: UpdateShopByListItem;
};

type AddViewAllModalProductToCarouselAction = {
  payload: {
    itemId: ItemId;
    lineNumber: LineNumber;
  };
};

type EstimatedPriceSuccessAction = {
  result: Pricing2PricedProductsResponse;
};

const getShopByListEligibilityRequest = (state: ShopByListState): ShopByListState => ({
  ...state,
  eligibility: { loading: true },
});

const getShopByListEligibilitySuccess = (
  state: ShopByListState,
  { result }: ShopByListEligibilitySuccessAction,
): ShopByListState => ({
  ...state,
  eligibility: { ...result, loading: false },
});

const getShopByListEligibilityFailure = (state: ShopByListState): ShopByListState => ({
  ...state,
  eligibility: {
    loading: false,
    status: STATUS_INELIGIBLE,
    reason: REASON_NOT_IN_COHORT,
  },
});

const getExtractedSearchTermsRequest = (
  state: ShopByListState,
  { searchType }: GetExtractedSearchTermsRequestAction,
): ShopByListState => {
  if (searchType === SEARCH_TYPE.BULK_ON_RESULTS_PAGE) {
    return {
      ...state,
      processing: {
        ...(state.processing as ShopByListProcessingActiveState),
        phase: ShopByListProcessingPhase.EXTRACTING_ADDITIONAL_SEARCH_TERMS,
      },
    };
  }

  return {
    ...state,
    processing: {
      phase: ShopByListProcessingPhase.EXTRACTING_SEARCH_TERMS,
      itemOrder: [],
      itemsById: {},
      recommendedSelection: [],
    },
  };
};

const getExtractedSearchTermsSuccess = (
  state: ShopByListState,
  { result, searchTermType, searchType }: GetExtractedSearchTermsSuccessAction,
): ShopByListState => {
  const append = searchType === SEARCH_TYPE.BULK_ON_RESULTS_PAGE;

  const itemOrder: ItemId[] = append ? [...state.processing.itemOrder] : [];
  const itemsById: Record<ItemId, ShopByListItemState> = append
    ? { ...state.processing.itemsById }
    : {};

  for (const { editedText, itemId, searchTerm } of result.parsedItems) {
    itemOrder.push(itemId);
    itemsById[itemId] = {
      candidates: [],
      itemId,
      originalSearchTerm: editedText,
      searchTerm,
      searchTermType,
      suggestionsStatus: ShopByListSuggestionsStatus.LOADING,
    };
  }

  const recommendedSelection = append ? [...state.processing.recommendedSelection] : [];
  const phase =
    result.parsedItems.length === 0 && !append
      ? ShopByListProcessingPhase.NO_RESULTS
      : ShopByListProcessingPhase.STREAMING_SUGGESTIONS;

  return {
    ...state,
    processing: {
      ...(append ? state.processing : {}),
      phase,
      itemOrder,
      itemsById,
      recommendedSelection,
    },
  };
};

const getSuggestionsSuccess = (
  state: ShopByListState,
  { result }: GetSuggestionsSuccessAction,
): ShopByListState => {
  // Success action can only occur after a request so `state.processing` is
  // guaranteed to exist.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const processing = state.processing! as ShopByListProcessingActiveState;
  const updatedItemsById = { ...processing.itemsById };

  for (const { itemId, candidates } of result.suggestions) {
    const item = updatedItemsById[itemId];

    updatedItemsById[itemId] = {
      ...item,
      suggestionsStatus: ShopByListSuggestionsStatus.SUCCESS,
      candidates,
    };
  }

  const incomingItemIds = new Set(result.suggestions.map(({ itemId }) => itemId));

  const updatedRecommendedSelection = [
    ...processing.recommendedSelection.filter(
      recommendation => !incomingItemIds.has(recommendation.key),
    ),
    ...result.recommendedSelection,
  ];

  return {
    ...state,
    processing: {
      ...processing,
      itemsById: updatedItemsById,
      recommendedSelection: updatedRecommendedSelection,
    },
  };
};

const updateShopByListSearchTerm = (
  state: ShopByListState,
  { payload: { itemId, searchTerm } }: UpdateSearchTermAction,
): ShopByListState => {
  // Update action can only occur after a request so `state.processing` is
  // guaranteed to exist.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const processing = state.processing! as ShopByListProcessingActiveState;
  const updatedItemsById = { ...processing.itemsById };

  const item = updatedItemsById[itemId];
  updatedItemsById[itemId] = {
    ...item,
    originalSearchTerm: searchTerm,
    searchTerm,
    searchTermType: SEARCH_TERM_TYPE.REFINED_TEXT,
    suggestionsStatus: ShopByListSuggestionsStatus.LOADING,
    candidates: [],
  };

  return {
    ...state,
    processing: {
      ...processing,
      itemsById: updatedItemsById,
    },
  };
};

const addViewAllModalProductToCarousel = (
  state: ShopByListState,
  { payload: { itemId, lineNumber } }: AddViewAllModalProductToCarouselAction,
): ShopByListState => {
  // View all action can only occur after a request so `state.processing` is
  // guaranteed to exist.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const processing = state.processing! as ShopByListProcessingActiveState;
  const item = processing.itemsById[itemId];

  if (item.candidates.some(({ searchProduct }) => searchProduct === lineNumber)) {
    return state;
  }

  return {
    ...state,
    processing: {
      ...processing,
      itemsById: {
        ...processing.itemsById,
        [itemId]: {
          ...item,
          candidates: [{ searchProduct: lineNumber }, ...item.candidates],
        },
      },
    },
  };
};

const getEstimatedPriceRequest = (state: ShopByListState) => ({
  ...state,
  processing: {
    ...state.processing,
    estimatedPriceLoading: true,
  },
});

const getEstimatedPriceSuccess = (
  state: ShopByListState,
  {
    result: [
      {
        totals: {
          totalPrice: { amount },
        },
      },
    ],
  }: EstimatedPriceSuccessAction,
) => ({
  ...state,
  processing: {
    ...state.processing,
    estimatedPriceLoading: false,
    estimatedPrice: amount,
  },
});

const getEstimatedPriceFailure = (state: ShopByListState) => ({
  ...state,
  processing: {
    ...state.processing,
    estimatedPriceLoading: false,
    estimatedPrice: undefined,
  },
});

type FetchViewAllModalSuggestionsRequestAction = {
  itemId: ItemId;
  requestId: string;
  searchTerm: string;
};

type FetchViewAllModalSuggestionsSuccessAction = {
  itemId: ItemId;
  requestId: string;
  result: TransformedSuggestionsResponse;
  searchTerm: string;
};

type FetchViewAllModalSuggestionsFailureAction = {
  itemId: ItemId;
  requestId: string;
  searchTerm: string;
};

const fetchViewAllModalSuggestionsRequest = (
  state: ShopByListState,
  { itemId, requestId, searchTerm }: FetchViewAllModalSuggestionsRequestAction,
): ShopByListState => ({
  ...state,
  viewAllModalProducts: {
    currentItemId: itemId,
    currentSearchTerm: searchTerm,
    hasError: false,
    isLoading: true,
    products: [],
    requestId,
  },
});

const fetchViewAllModalSuggestionsSuccess = (
  state: ShopByListState,
  { itemId, requestId, result, searchTerm }: FetchViewAllModalSuggestionsSuccessAction,
): ShopByListState => {
  if (state.viewAllModalProducts.requestId !== requestId) {
    return state;
  }

  const products = result.suggestions[0]?.candidates ?? [];

  return {
    ...state,
    viewAllModalProducts: {
      currentItemId: itemId,
      currentSearchTerm: searchTerm,
      hasError: false,
      isLoading: false,
      products,
      requestId,
    },
  };
};

const fetchViewAllModalSuggestionsFailure = (
  state: ShopByListState,
  { requestId }: FetchViewAllModalSuggestionsFailureAction,
): ShopByListState => {
  if (state.viewAllModalProducts.requestId !== requestId) {
    return state;
  }

  return {
    ...state,
    viewAllModalProducts: {
      ...state.viewAllModalProducts,
      hasError: true,
      isLoading: false,
      products: [],
    },
  };
};

const resetViewAllModalSuggestions = (state: ShopByListState): ShopByListState => ({
  ...state,
  viewAllModalProducts: {
    hasError: false,
    isLoading: true,
    products: [],
  },
});

const actionTypeReducerMap = [
  [GET_SHOP_BY_LIST_ELIGIBILITY.request, getShopByListEligibilityRequest],
  [GET_SHOP_BY_LIST_ELIGIBILITY.success, getShopByListEligibilitySuccess],
  [GET_SHOP_BY_LIST_ELIGIBILITY.failure, getShopByListEligibilityFailure],
  [GET_SHOP_BY_LIST_EXTRACTED_SEARCH_TERMS.request, getExtractedSearchTermsRequest],
  [GET_SHOP_BY_LIST_EXTRACTED_SEARCH_TERMS.success, getExtractedSearchTermsSuccess],
  [GET_SHOP_BY_LIST_SUGGESTIONS.success, getSuggestionsSuccess],
  [UPDATE_SHOP_BY_LIST_SEARCH_TERM, updateShopByListSearchTerm],
  [GET_SHOP_BY_LIST_ESTIMATED_PRICE.request, getEstimatedPriceRequest],
  [GET_SHOP_BY_LIST_ESTIMATED_PRICE.success, getEstimatedPriceSuccess],
  [GET_SHOP_BY_LIST_ESTIMATED_PRICE.failure, getEstimatedPriceFailure],
  [FETCH_VIEW_ALL_MODAL_SUGGESTIONS.request, fetchViewAllModalSuggestionsRequest],
  [FETCH_VIEW_ALL_MODAL_SUGGESTIONS.success, fetchViewAllModalSuggestionsSuccess],
  [FETCH_VIEW_ALL_MODAL_SUGGESTIONS.failure, fetchViewAllModalSuggestionsFailure],
  [RESET_VIEW_ALL_MODAL_SUGGESTIONS, resetViewAllModalSuggestions],
  [ADD_VIEW_ALL_MODAL_PRODUCT_TO_CAROUSEL, addViewAllModalProductToCarousel],
] as const;

export type PayloadMap = ReducerMapTupleToRecord<typeof actionTypeReducerMap>;
export default createReducer(initialState, actionTypeReducerMap);
